<template>
  <div>
    <CButtonGroup>
      <!-- First Page -->
      <CButton color="gray-400" variant="outline" @click="goToPage(1)">
        <span><font-awesome-icon :icon="['fa', 'backward']" /></span>
      </CButton>

      <!-- 1 Page back -->
      <CButton
        color="gray-400"
        variant="outline"
        @click="goToPage(currentPage - 1)"
      >
        <span><font-awesome-icon :icon="['fa', 'caret-left']" /></span>
      </CButton>

      <!-- Show ellipsis at start if needed -->
      <CButton
        v-if="showStartEllipsis"
        color="gray-400"
        variant="outline"
        disabled
      >
        <span>...</span>
      </CButton>

      <!-- Pages -->
      <CButton
        v-for="page in visiblePages"
        :key="page"
        :class="
          page === currentPage
            ? 'border-primary bg-primary text-white'
            : 'gray-400'
        "
        color="gray-400"
        variant="outline"
        @click="goToPage(page)"
      >
        <span>{{ page }}</span>
      </CButton>

      <!-- Show ellipsis at end if needed -->
      <CButton
        v-if="showEndEllipsis"
        color="gray-400"
        variant="outline"
        disabled
      >
        <span>...</span>
      </CButton>

      <!-- 1 Page forward -->
      <CButton
        color="gray-400"
        variant="outline"
        @click="goToPage(currentPage + 1)"
      >
        <span><font-awesome-icon :icon="['fa', 'caret-right']" /></span>
      </CButton>

      <!-- Last Page -->
      <CButton color="gray-400" variant="outline" @click="goToPage(pages)">
        <span><font-awesome-icon :icon="['fa', 'forward']" /></span>
      </CButton>
    </CButtonGroup>
  </div>
</template>

<script>
/**
 * Pagination component for navigating through paginated data.
 * Expects a `data` prop with `count`, `limit`, and `offset` properties.
 * The `paginate` event is emitted with the selected page number when a page button is clicked.
 * The `maxVisiblePages` prop controls how many page buttons are shown at once.
 * If the total number of pages exceeds `maxVisiblePages`, ellipses are shown to indicate skipped pages.
 */
export default {
  name: "Pagination",
  props: {
    data: Object,
    maxVisiblePages: {
      type: Number,
      default: 10,
    },
  },
  computed: {
    pages() {
      return Math.ceil(this.data.count / this.data.limit);
    },
    currentPage() {
      if (this.data.offset === 0) {
        return 1;
      }
      return Math.floor(this.data.offset / this.data.limit) + 1;
    },
    visiblePages() {
      if (this.pages <= this.maxVisiblePages) {
        return Array.from({ length: this.pages }, (_, i) => i + 1);
      }

      let start = Math.max(
        1,
        this.currentPage - Math.floor(this.maxVisiblePages / 2)
      );
      let end = start + this.maxVisiblePages - 1;

      if (end > this.pages) {
        end = this.pages;
        start = Math.max(1, end - this.maxVisiblePages + 1);
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    showStartEllipsis() {
      return this.visiblePages[0] > 1;
    },

    showEndEllipsis() {
      return this.visiblePages[this.visiblePages.length - 1] < this.pages;
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.pages) {
        this.$emit("paginate", page);
        //localStorage.setItem("l_prod_page", parseInt(page));
      }
    },
  },
};
</script>
